import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Archive, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api from "../../lib/api";
import { money } from "../../lib/format";

// Shared goal-based manager for Savings and Emergency wallets.
export default function GoalManager({ open, onOpenChange, month, currency, onChanged, kind, readOnly }) {
  const isSavings = kind === "savings";
  const endpoint = isSavings ? "savings" : "emergency";
  const depAction = isSavings ? "deposit" : "contribute";
  const title = isSavings ? "Savings Wallet" : "Emergency Fund Wallet";
  const addLabel = isSavings ? "goal" : "category";
  const accent = isSavings ? "#0D9488" : "#0284C7";

  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [depFor, setDepFor] = useState(null);
  const [depAmt, setDepAmt] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/${endpoint}`);
    setItems(data);
  }, [endpoint]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const guard = () => { if (readOnly) { toast.error("View-only account — editing is disabled."); return true; } return false; };

  const add = async () => {
    if (guard()) return;
    if (!name || !target) return toast.error(`Enter ${addLabel} name and target`);
    await api.post(`/${endpoint}`, { name, target: Number(target) });
    setName(""); setTarget("");
    toast.success(`${isSavings ? "Goal" : "Category"} created`);
    load(); onChanged();
  };

  const deposit = async (id) => {
    if (guard()) return;
    if (!depAmt) return;
    await api.post(`/${endpoint}/${id}/${depAction}`, { amount: Number(depAmt), month });
    setDepFor(null); setDepAmt("");
    toast.success("Contribution recorded");
    load(); onChanged();
  };

  const archive = async (id, archived) => {
    if (guard()) return;
    await api.put(`/${endpoint}/${id}`, { archived: !archived });
    load(); onChanged();
  };

  const del = async (id) => {
    if (guard()) return;
    await api.delete(`/${endpoint}/${id}`);
    load(); onChanged();
  };

  const visible = items.filter((i) => showArchived ? true : !i.archived);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>{isSavings ? "Create savings goals and track deposits toward each target." : "Create emergency categories and track contributions. Balances persist across months."}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 items-end bg-slate-50 p-3 rounded-xl">
          <Input data-testid={`${endpoint}-name-input`} placeholder={isSavings ? "e.g. Vacation Fund" : "e.g. Medical Buffer"} className="flex-1 min-w-[160px]" value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
          <Input data-testid={`${endpoint}-target-input`} type="number" placeholder="Target" className="w-32 font-mono-nums" value={target} onChange={(e) => setTarget(e.target.value)} disabled={readOnly} />
          <Button data-testid={`${endpoint}-add-button`} onClick={add} disabled={readOnly} className="text-white" style={{ background: accent }}><Plus className="w-4 h-4" /></Button>
        </div>

        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">Contributing to <span className="font-medium">{month}</span></p>
          <button onClick={() => setShowArchived(!showArchived)} className="text-xs text-slate-500 hover:text-slate-800" data-testid={`${endpoint}-toggle-archived`}>
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>

        <div className="space-y-3">
          {visible.map((g) => {
            const pct = g.target > 0 ? Math.min(Math.round((g.actual / g.target) * 100), 100) : 0;
            return (
              <div key={g.id} data-testid={`${endpoint}-row-${g.id}`} className={`p-4 rounded-xl border ${g.archived ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-800 text-sm">{g.name}{g.archived && <span className="text-xs text-slate-400 ml-2">(archived)</span>}</p>
                  <span className="font-mono-nums text-sm font-semibold" style={{ color: accent }}>{money(g.actual, currency)} <span className="text-slate-400 font-normal">/ {money(g.target, currency)}</span></span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-2.5">
                  <div className="wallet-progress h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
                </div>
                <div className="flex items-center justify-between mt-2.5">
                  <span className="text-xs text-slate-400">{pct}% funded</span>
                  <div className="flex items-center gap-1">
                    {depFor === g.id ? (
                      <div className="flex items-center gap-1">
                        <Input autoFocus data-testid={`${endpoint}-deposit-input-${g.id}`} type="number" placeholder="Amount" className="w-24 h-8 font-mono-nums" value={depAmt} onChange={(e) => setDepAmt(e.target.value)} />
                        <Button size="sm" className="h-8 text-white" style={{ background: accent }} onClick={() => deposit(g.id)} data-testid={`${endpoint}-deposit-confirm-${g.id}`}>Add</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="h-8" onClick={() => { setDepFor(g.id); setDepAmt(""); }} data-testid={`${endpoint}-deposit-${g.id}`}>
                        <TrendingUp className="w-3.5 h-3.5 mr-1" /> Contribute
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" onClick={() => archive(g.id, g.archived)} data-testid={`${endpoint}-archive-${g.id}`}><Archive className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(g.id)} data-testid={`${endpoint}-delete-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
          {visible.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Nothing here yet. Create your first {addLabel} above.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
